import { DocumentButtons } from 'components/common-form/sidePanel/docToolbar/documents/DocumentButtons';
import { useAppSelector } from 'hooks/useAppSelector';
import { useCrossBrowserContext } from 'hooks/useCrossBrowserContext';
import { useToggleFeature } from 'hooks/useToggleFeature';
import type { DocumentType } from 'models/documents/DocumentType';
import { useState } from 'react';
import { isPendingEnrichment } from 'state/selectors';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

import { AllDocumentsLegacy } from './AllDocuments.legacy';
import { DocumentList } from './design-system/DocumentList';
import { KidDocuments } from './documents/KidDocuments';
import { ProductGovernanceDocuments } from './documents/ProductGovernanceDocuments';
import { TermsheetDocuments } from './documents/TermsheetDocuments';
import { getVisibleDocumentCount } from './selectors';

interface Props {
  className?: string;
  tabId: string;
  workspaceType: QuoteWorkspaceType;
  additionnalDocumentTypes: DocumentType[];
}

function useDocumentCount(
  tabId: string,
  workspaceType: QuoteWorkspaceType,
  docTypes: Props['additionnalDocumentTypes'],
) {
  const regulatoryDocumentsCount = useAppSelector(state => getVisibleDocumentCount(state, workspaceType, tabId));
  const additonnalDocumentsCount = docTypes.length;
  return regulatoryDocumentsCount + additonnalDocumentsCount;
}

function useDocumentEnabled(tabId: string) {
  const { isDirty, isLocked } = useCrossBrowserContext();
  const isEnriching = useAppSelector(state => isPendingEnrichment(state, tabId));
  const isDocumentEnabled = !isEnriching && !isDirty && !isLocked;
  return isDocumentEnabled;
}

export function AllDocuments({ tabId, workspaceType, additionnalDocumentTypes }: Props) {
  const isNextDocumentEnabled = useToggleFeature('nextDocumentParis');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const totalDocumentCount = useDocumentCount(tabId, workspaceType, additionnalDocumentTypes);
  const isDocumentEnabled = useDocumentEnabled(tabId);

  if (!isNextDocumentEnabled) {
    return <AllDocumentsLegacy tabId={tabId} workspaceType={workspaceType} />;
  }

  if (!isDocumentEnabled) {
    return null;
  }

  return (
    <DocumentList count={totalDocumentCount} isCollapsed={isCollapsed} toggleCollapse={() => setIsCollapsed(c => !c)}>
      {additionnalDocumentTypes.length > 0 && (
        <DocumentButtons documents={additionnalDocumentTypes} tabId={tabId} workspaceType={workspaceType} />
      )}
      <TermsheetDocuments tabId={tabId} workspaceType={workspaceType} />
      <KidDocuments tabId={tabId} workspaceType={workspaceType} />
      <ProductGovernanceDocuments tabId={tabId} workspaceType={workspaceType} />
    </DocumentList>
  );
}
